import { safeHttpUrl, type EcwIdeaReference } from '@citizen-walk/shared-types';
import { useState } from 'react';

/**
 * 処方箋カード AI 参考事例 grid (Sprint AW、旧 IdeaForm `relevantReferences` 踏襲)。
 *
 * 旧 v1 では IdeaForm.tsx 内で LLM が返した `references[]` を縦並びリストで描画していたが、
 * v2 minimum では shared-ui Modal の幅制約内で 2-3 column の grid を採用 (画像主体)。
 *
 * a11y: 外部 link は `target="_blank" rel="noopener noreferrer"`、image は alt + lazy load + 壊れ画像 fallback。
 *
 * Variant:
 *   - 'compact': IdeaWorkspace 内のチャットバブル / review section 用 (画像 96px、最大 5 件横スクロール)
 *   - 'detail':  IdeaDetailModal 用 (画像 140px、grid-cols-2 md:grid-cols-3)
 */
export interface ReferenceGridProps {
  references: EcwIdeaReference[];
  variant?: 'compact' | 'detail';
}

export function ReferenceGrid({ references, variant = 'detail' }: ReferenceGridProps) {
  if (!references || references.length === 0) return null;
  const gridClass =
    variant === 'compact'
      ? 'mt-1 grid grid-cols-2 sm:grid-cols-3 gap-2'
      : 'mt-1 grid grid-cols-2 md:grid-cols-3 gap-3';

  return (
    <ul className={gridClass} aria-label="AI 提案の参考事例" data-testid="reference-grid">
      {references.map((r, i) => (
        // Sprint AW reviewer fixup: index key → url/title 優先 (LLM 生成で並び替え無いが、
        // 重複時の React 警告抑制 + 将来 reorder 対応のため、url ?? title ?? index で fallback)
        <ReferenceCard key={r.url ?? r.title ?? `ref-${i}`} reference={r} variant={variant} />
      ))}
    </ul>
  );
}

function ReferenceCard({
  reference,
  variant,
}: {
  reference: EcwIdeaReference;
  variant: 'compact' | 'detail';
}) {
  const [imgError, setImgError] = useState(false);
  const imgHeightClass = variant === 'compact' ? 'h-20' : 'h-28';
  const showImage = reference.imageUrl && !imgError;

  return (
    <li
      className="rounded border border-slate-200 bg-white overflow-hidden flex flex-col"
      data-testid="reference-card"
    >
      <div
        className={`relative ${imgHeightClass} bg-slate-100 flex items-center justify-center overflow-hidden`}
      >
        {showImage ? (
          <img
            src={reference.imageUrl}
            alt={reference.title}
            loading="lazy"
            onError={() => setImgError(true)}
            className="w-full h-full object-cover"
          />
        ) : (
          <span className="text-slate-400 text-2xl" aria-hidden="true">
            🖼
          </span>
        )}
      </div>
      <div className="p-1.5 flex-1 flex flex-col gap-0.5">
        <p
          className={`font-semibold ${variant === 'compact' ? 'text-[11px]' : 'text-xs'} text-slate-800 break-words line-clamp-2`}
          title={reference.title}
        >
          {reference.title}
        </p>
        {reference.description && variant === 'detail' && (
          <p className="text-[10px] text-slate-600 line-clamp-2">{reference.description}</p>
        )}
        {/* 参考 URL は AI / 利用者由来。http(s) 以外はリンクにしない (docs/13 §7) */}
        {safeHttpUrl(reference.url) && (
          <a
            href={safeHttpUrl(reference.url) ?? undefined}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[10px] text-primary-700 hover:text-primary-900 underline mt-auto inline-flex items-center gap-0.5"
            aria-label={`参考リンクを新しいタブで開く: ${reference.title}`}
          >
            🔗 参考リンク
          </a>
        )}
      </div>
    </li>
  );
}
